import { BetterFetchOption } from 'better-auth/react';
import { useEffect } from 'react';
import { Loading, useAuthProvider, useNotify } from 'react-admin';
import { type To, useNavigate, useSearchParams } from 'react-router-dom';

import { setDefaultAdminActive } from '@Components/LoginPageForm';
import { authClient } from '@Utils/authClient';
import { STORAGE_KEYS } from '@Utils/storage_keys';

export function AuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const authProvider = useAuthProvider();

  useEffect(() => {
    localStorage.removeItem(STORAGE_KEYS.activeAdminId);
    localStorage.removeItem(STORAGE_KEYS.adminsList);

    const type = params.get('type');
    const token = params.get('token');
    let redirectUrl = params.get('redirectUrl');

    if (type === null || token === null) {
      navigate('/', { replace: true });
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;

    const verifyToken = async () => {
      let redirectAfterLogin = false;
      const fetchOptions: BetterFetchOption = {
        signal,
        onSuccess({ response: { url } }) {
          const { searchParams } = new URL(url);

          // Success does not actually mean success
          // Better-auth often handles error by redirecting to: ${callbackURL}?error=${errorCode}
          const errorCode = searchParams.get('error');
          if (errorCode === null) {
            navigate(getSuccessRedirect(redirectUrl), { replace: true });
            return;
          }

          // oxlint-disable-next-line no-console
          // eslint-disable-next-line no-console
          console.error(`Link validation failed: ${errorCode}`);

          // Redirect to login page potentially preserving the page that should be loaded afterwards
          navigate('/login', {
            replace: true,
            state: redirectAfterLogin ? getLoginRedirectState(redirectUrl) : undefined,
          });
          notify('login_page.messages.link_expired', { type: 'error' });
        },
        onError({ error }) {
          // oxlint-disable-next-line no-console
          // eslint-disable-next-line no-console
          console.error(error);

          // Redirect to login page potentially preserving the page that should be loaded afterwards
          navigate('/login', {
            replace: true,
            state: redirectAfterLogin ? getLoginRedirectState(redirectUrl) : undefined,
          });
          notify('login_page.messages.link_expired', { type: 'error' });
        },
      };

      if (type === 'magic-link') {
        // Verify magic link is still valid, and regardless of the result preserve the page we should be redirected to after successful login
        // This allow the user to use expired link to login normally, but still get to the page they email CTA asked for
        redirectAfterLogin = true;
        await authClient.magicLink.verify(
          {
            query: {
              token,
            },
          },
          {
            ...fetchOptions,
            async onSuccess(context) {
              await setDefaultAdminActive();
              return fetchOptions.onSuccess?.(context);
            },
          },
        );
      } else if (type === 'reset-password') {
        // Check reset password link has not expired yet, and redirect to change password page on success
        await authClient.$fetch<{
          twoFactorRedirect: boolean;
          phoneNumber?: string | null;
          alternateEmail?: string | null;
        }>(`/reset-password/${token}`, {
          method: 'GET',
          query: {
            callbackURL: '/',
          },
          ...fetchOptions,
          async onSuccess(context) {
            const params = new URLSearchParams({ token });
            if (context.data.twoFactorRedirect) {
              if (context.data.alternateEmail) {
                params.set('alternateEmail', context.data.alternateEmail);
              } else {
                params.set('phoneNumber', String(context.data.phoneNumber));
              }
            }
            redirectUrl = `/change_password?${params.toString()}`;
            return fetchOptions.onSuccess?.(context);
          },
          async onError(context) {
            if (
              context.error.status === 403 &&
              context.error.message === 'Password reset disabled'
            ) {
              // oxlint-disable-next-line no-console
              // eslint-disable-next-line no-console
              console.error(context.error);

              // Redirect to login page potentially preserving the page that should be loaded afterwards
              navigate('/login', {
                replace: true,
                state: redirectAfterLogin ? getLoginRedirectState(redirectUrl) : undefined,
              });
              notify('login_page.messages.change_password.disabled', { type: 'error' });
              return;
            }

            return fetchOptions.onError?.(context);
          },
        });
      } else if (type === 'verify-email') {
        // Mark user's email as verified and redirect them to login
        await authClient.$fetch('/verify-email', {
          method: 'GET',
          query: {
            token,
            callbackURL: '/',
          },
          ...fetchOptions,
          async onSuccess(context) {
            try {
              await authProvider.checkAuth({});
            } catch (error) {
              // oxlint-disable-next-line no-console
              // eslint-disable-next-line no-console
              console.error(error);
            }
            return fetchOptions.onSuccess?.(context);
          },
        });
      } else if (type === 'change-email') {
        try {
          // User must be authenticated for change email link to work
          await authProvider.checkAuth({});
          redirectUrl = '/verify_email';
          // change-email has the same verification URL as verify-email
          await authClient.$fetch('/verify-email', {
            method: 'GET',
            query: {
              token,
              callbackURL: `${window.location.origin}/#/auth-callback?type=verify-email`,
            },
            async onResponse() {
              await authProvider.logout({});
            },
            ...fetchOptions,
          });
          // verify-email page is only accessible if a user is logged out
          // they will get logged in once they verify their new email
          await authProvider.logout({});
        } catch {
          // User is not authenticated - login and then redirect back here
          navigate('/login', {
            replace: true,
            state: {
              nextPathname: '/auth-callback',
              nextSearch: `?${params.toString()}`,
            },
          });
          notify('ra.auth.auth_check_error', { type: 'error' });
        }
      } else if (type === 'ghost-challenge') {
        const { data } = await authClient.$fetch<{ callbackURL: string }>(
          '/impersonation/challenge',
          {
            method: 'POST',
            body: {
              state: token,
              callbackURL: document.referrer,
            },
            ...fetchOptions,
            onSuccess({ data }) {
              window.location.replace(data.callbackURL);
            },
          },
        );
        if (data) {
          window.location.replace(data.callbackURL);
        }
      } else if (type === 'impersonation') {
        await authClient.$fetch('/impersonation/impersonate-user', {
          method: 'POST',
          body: {
            token,
          },
          ...fetchOptions,
          async onSuccess(context) {
            await setDefaultAdminActive();
            return fetchOptions.onSuccess?.(context);
          },
        });
      } else {
        // Unknown callback type. In the future we might want to handle OAuth callbacks here.
        // But for now, just redirect to login page.
        navigate('/', { replace: true });
      }
    };

    verifyToken();

    // Cancel any requests if component unmounts
    return () => {
      controller.abort();
    };
  }, [navigate, notify, authProvider, params]);

  return <Loading />;
}

function getSuccessRedirect(redirectUrl: string | null): To {
  if (redirectUrl === null || redirectUrl.startsWith('http')) {
    return '/';
  }

  const [path, search] = redirectUrl.split('?');
  const state: To = {
    pathname: path,
  };

  if (search) {
    state.search = search;
  }

  return state;
}

function getLoginRedirectState(redirectUrl: string | null) {
  if (redirectUrl === null || redirectUrl.startsWith('http')) {
    return undefined;
  }

  const [path, search] = redirectUrl.split('?');
  const state: { nextPathname: string; nextSearch?: string } = {
    nextPathname: path,
  };

  if (search) {
    state.nextSearch = `?${search}`;
  }

  return state;
}
